/**
 * FamilyNetworkGraph — SVG hub-and-spoke visualisation of this family
 * and its marriage-linked families.  Deliberately simple (no D3
 * dependency): places the root family at the centre and lays out
 * related families around a ring using basic trig.  Additional depth-2
 * families sit on an outer ring.
 *
 * Props:
 *   treeId       — root tree id
 *   className    — outer wrapper classes
 *   height       — SVG height (default 320px)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import { GitBranch, Loader2, Users } from "lucide-react";

export default function FamilyNetworkGraph({ treeId, className = "", height = 320 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    http.get(`/family-trees/${treeId}/network-graph?depth=2`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [treeId]);

  if (loading) return <div className={className} style={{ height }}>
    <div className="h-full flex items-center justify-center opacity-60"><Loader2 className="w-5 h-5 animate-spin" /></div>
  </div>;
  if (!data || data.node_count === 0) return null;

  const width = 520;
  const cx = width / 2, cy = height / 2;
  const R1 = Math.min(width, height) * 0.32;   // 1-hop ring radius
  const R2 = Math.min(width, height) * 0.46;   // 2-hop ring radius

  // Layout: center = root, then ring 1 = "related", ring 2 = "network".
  const center = data.nodes.find((n) => n.role === "center");
  const related = data.nodes.filter((n) => n.role === "related");
  const network = data.nodes.filter((n) => n.role === "network");

  const positioned = new Map();
  if (center) positioned.set(center.id, { ...center, x: cx, y: cy });
  related.forEach((n, i) => {
    const a = (i / Math.max(related.length, 1)) * Math.PI * 2 - Math.PI / 2;
    positioned.set(n.id, { ...n, x: cx + R1 * Math.cos(a), y: cy + R1 * Math.sin(a) });
  });
  network.forEach((n, i) => {
    const a = (i / Math.max(network.length, 1)) * Math.PI * 2 - Math.PI / 4;
    positioned.set(n.id, { ...n, x: cx + R2 * Math.cos(a), y: cy + R2 * Math.sin(a) });
  });

  return (
    <div className={className} data-testid="fmc-network-graph">
      <div className="flex items-center gap-2 mb-2">
        <GitBranch className="w-4 h-4" style={{ color: "#4338CA" }} />
        <h3 className="font-display text-lg">Family Network</h3>
        <span className="text-xs opacity-60">— {data.node_count} famil{data.node_count === 1 ? "y" : "ies"} within 2 marriage hops</span>
      </div>
      <div
        className="rounded-2xl border overflow-hidden"
        style={{
          borderColor: "var(--wofy-border)",
          background: "linear-gradient(135deg, #F8FAFC 0%, #F0F9FF 100%)",
        }}
      >
        <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} style={{ display: "block" }}>
          {/* Edges */}
          {data.edges.map((e, i) => {
            const a = positioned.get(e.a), b = positioned.get(e.b);
            if (!a || !b) return null;
            return (
              <line
                key={i}
                x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                stroke="#4338CA55"
                strokeWidth="1.5"
                strokeDasharray={e.verification === "pending" ? "4 3" : "0"}
              />
            );
          })}
          {/* Nodes */}
          {Array.from(positioned.values()).map((n) => {
            const isCenter = n.role === "center";
            const r = isCenter ? 30 : 22;
            const label = n.fam_id || n.name;
            return (
              <a key={n.id} href={`/family/${n.id}/manage`} data-testid={`fmc-graph-node-${n.id}`}>
                <circle
                  cx={n.x} cy={n.y} r={r}
                  fill={isCenter ? "#4338CA" : (n.role === "related" ? "#DB2777" : "#0891B2")}
                  opacity={isCenter ? 1 : 0.85}
                />
                <text
                  x={n.x} y={n.y + 4}
                  textAnchor="middle"
                  fontSize={isCenter ? 12 : 10}
                  fontFamily="ui-monospace, SFMono-Regular, monospace"
                  fill="white"
                  fontWeight="700"
                >
                  {label}
                </text>
                <text
                  x={n.x} y={n.y + r + 12}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#1E1B4B"
                  fontWeight="500"
                >
                  {(n.name || "").length > 22 ? (n.name || "").slice(0, 20) + "…" : n.name}
                </text>
              </a>
            );
          })}
        </svg>
      </div>
      <div className="flex items-center gap-3 mt-2 text-xs opacity-70">
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full" style={{ background: "#4338CA" }} />center</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full" style={{ background: "#DB2777" }} />related (1 hop)</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full" style={{ background: "#0891B2" }} />network (2+ hops)</span>
      </div>
    </div>
  );
}
